import type { StructuredEvidenceValue } from '../../types/siteEvidence';

/**
 * A utility letter / agreement noun ("Capacity Letter", "Interconnection
 * Agreement", "will-serve letter"). A gas, water, sewer or telecom service
 * agreement is not an electric utility letter.
 */
const UTILITY_LETTER_NOUN_RE =
  /(?<!\b(?:gas|water|sewer|wastewater|fiber|telecom|pipeline|transportation)[\s-])\b(?:capacity|service|will[- ]serve|interconnection|commitment|utility)\s+(?:letters?|agreements?)\b/i;
/** A question that asks whether such a letter has been issued or exists. */
const LETTER_ISSUED_OR_EXISTS_RE =
  /\b(?:issued|provided|signed|executed|sent|granted|received|obtained|in\s+place|exists?|on\s+file)\b/i;
/**
 * A question about what a letter REQUIRES or CONTAINS, not whether one exists:
 * "Is a signed service agreement required before the facilities study? No."
 */
const LETTER_REQUIREMENT_QUESTION_RE =
  /\b(?:required|requires?|need(?:ed|s)?|necessary|includes?|included|cover(?:s|ed)?|allow(?:s|ed)?|permit(?:s|ted)?|should|must)\b/i;
const NON_ELECTRIC_SERVICE_RE = /\b(?:gas|water|wastewater|sewer|pipeline|fiber|telecom)\b/i;
/**
 * Each question with the answer "No" / "Not yet" / "None" after it, on the
 * same or the next line, optionally as "A: No". `[^\S\r\n]` is any space but a
 * line break: the live Holly Ridge Q&A has a no-break space after the "?".
 */
const QUESTION_ANSWERED_NO_RE =
  /([^?\n]{1,240})\?[^\S\r\n]*(?:\r?\n[^\S\r\n]*)?(?:A(?:nswer)?[^\S\r\n]*[:.)\u2013\u2014-][^\S\r\n]*)?(?:no|not\s+yet|not\s+at\s+this\s+time|none)\b/gi;
/** The answer that follows a question ("? No." / "?\nNo." / "? A: Not yet."), matched sticky at the question's end. */
const ANSWER_NO_AT_RE =
  /[^\S\r\n]*(?:\r?\n[^\S\r\n]*)?(?:A(?:nswer)?[^\S\r\n]*[:.)\u2013\u2014-][^\S\r\n]*)?(?:no|not\s+yet|not\s+at\s+this\s+time|none)\b/iy;
/** The document states that no utility letter / agreement has been issued. */
const NO_UTILITY_LETTER_STATEMENT_RE = new RegExp([
  // "Entergy has not (yet) issued a (formal) will-serve letter"
  String.raw`\b(?:has|have)\s+not\s+(?:yet\s+)?(?:issued|provided|signed|executed|sent|granted)\s+(?:(?:a|an|any)\s+)?(?:formal\s+|written\s+)?(?:capacity|service|will[- ]serve|interconnection)\s+(?:letters?|agreements?)\b`,
  // "We have not received a will-serve letter"
  String.raw`\b(?:has|have)\s+not\s+(?:yet\s+)?(?:received|obtained)\s+(?:(?:a|an|any)\s+)?(?:formal\s+|written\s+)?(?:capacity|service|will[- ]serve|interconnection)\s+(?:letters?|agreements?)\b`,
  // "A formal capacity letter has not been issued"
  String.raw`(?<!\b(?:gas|water|sewer|wastewater|fiber|telecom|pipeline|transportation)[\s-])\b(?:capacity|service|will[- ]serve|interconnection)\s+(?:letters?|agreements?)\s+(?:has|have)\s+not\s+(?:yet\s+)?been\s+(?:issued|provided|signed|executed|received|granted)\b`,
  // "No capacity letter exists" / "No will-serve letter has been issued" / "no service agreement in place"
  String.raw`\bno\s+(?:formal\s+|written\s+)?(?:capacity|service|will[- ]serve)\s+(?:letters?|agreements?)\s+(?:(?:has|have)\s+(?:yet\s+)?been\s+(?:issued|received)|exists?|(?:is\s+)?(?:yet|in\s+place))\b`,
].join('|'), 'i');
/**
 * An offer, commitment or ability to serve electric supply. Used only on a
 * statement (never a question) whose offer word is not negated.
 */
const UTILITY_OFFER_RE = new RegExp([
  String.raw`\b(?:offers?|offered|commits?|committed\s+to|allocat(?:e|es|ed)|confirms?)\b`,
  String.raw`\b(?:can|will)\s+(?:serve|supply|deliver|provide|accommodate)\b`,
  // "the Cooperative is able to provide 150 MW"
  String.raw`\b(?:is|are)\s+(?:able|prepared|willing)\s+to\s+(?:serve|supply|deliver|provide|accommodate)\b`,
  // "has sufficient transmission capacity to serve the requested 300 MW load"
  String.raw`\bcapacity\b[^.?!\n]{0,40}?\bto\s+(?:serve|supply|accommodate)\b`,
  // "has 300 MW of electric capacity available to serve the site"
  String.raw`\bavailable\s+to\s+(?:serve|supply|deliver)\b`,
  String.raw`\baccommodat(?:e|es|ed|ing)\b`,
].join('|'), 'gi');
/** A negator directly before (at most two words) an offer word: "has not offered", "cannot accommodate". */
const OFFER_NEGATOR_BEFORE_RE = /(?:\b(?:not|never|no|cannot|unable\s+to)\b|n['\u2019]t)\s+(?:\w+\s+){0,2}$/i;
const OFFER_NEGATOR_INSIDE_RE = /\b(?:not|never|no|cannot)\b|n['\u2019]t\b/i;
/**
 * A customer schedule row: "<label>: [approximately] <N> MW", e.g.
 * "Phase 3 (Mid-2032): Approximately 1,000MW (Entergy Grid Power, Battery)".
 */
const SCHEDULE_ROW_RE =
  /^\s*(?:[-\u2022*\u00b7\u25aa]\s*)?(?:phase|stage|block|step|tranche|building|initial|interim|final|ultimate|full|year|fy|(?:19|20)\d{2}|q[1-4]|h[12]|mid|early|late)\b[^:\n]{0,60}:\s*(?:approximately|approx\.?|about|around|roughly|up\s+to|~|\u2248)?\s*\d[\d,]*(?:\.\d+)?\s*MW\b/i;
/**
 * A header that introduces the CUSTOMER's load ramp or schedule
 * ("Richland Parish Load ramp:"). A generic "data center load" or "requested
 * load" mention is not one: utility letters quote the request they answer.
 */
const CUSTOMER_LOAD_HEADER_RE =
  /\b(?:load\s+(?:ramp|schedule|forecast|profile|request|study|growth)|(?:ramp|demand)\s+schedule|(?:IT|facility|campus|customer|data[- ]cent(?:er|re))\s+(?:load|demand)\s+(?:ramp|schedule|forecast|profile|plan))\b/i;
const MW_FIGURE_RE = /(\d[\d,]*(?:\.\d+)?)\s*MW\b/gi;

/**
 * Line and sentence boundaries of one document body, built once per filter
 * call: bodies can be a whole extracted PDF (`fullExtractedText`) with
 * hundreds of MW figures, so each lookup is a binary search, not a rescan.
 */
interface BodyIndex {
  body: string;
  lines: string[];
  lineStarts: number[];
  /** Offsets of sentence ends: a line break, or . ? ! before any space (a no-break space included) or the end. */
  sentenceEnds: number[];
}

function indexBody(body: string): BodyIndex {
  const lines = body.split('\n');
  const lineStarts: number[] = [];
  let offset = 0;
  for (const line of lines) {
    lineStarts.push(offset);
    offset += line.length + 1;
  }
  const sentenceEnds = [...body.matchAll(/[.?!](?=\s|$)|\n/g)].map(match => match.index!);
  return { body, lines, lineStarts, sentenceEnds };
}

/** Index of the last entry of an ascending list that is <= `value` (-1 when none). */
function lastAtOrBefore(sorted: readonly number[], value: number): number {
  let low = 0;
  let high = sorted.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (sorted[mid]! <= value) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return found;
}

/** The sentence containing `index`, with the offset just past its end. */
function sentenceRangeAt(ix: BodyIndex, index: number): { text: string; end: number } {
  const before = lastAtOrBefore(ix.sentenceEnds, index - 1);
  const start = before >= 0 ? ix.sentenceEnds[before]! + 1 : 0;
  const next = ix.sentenceEnds[before + 1];
  const end = next != null ? next + 1 : ix.body.length;
  return { text: ix.body.slice(start, end), end };
}

/** Whether a statement (not a question) offers, commits or is able to serve, with the offer word not negated. */
function statesOffer(sentence: string): 'offer' | 'negated' | 'none' {
  if (/\?\s*$/.test(sentence)) return 'none';
  const offers = [...sentence.matchAll(UTILITY_OFFER_RE)];
  if (offers.length === 0) return 'none';
  return offers.some(offer => !OFFER_NEGATOR_BEFORE_RE.test(sentence.slice(Math.max(0, offer.index! - 30), offer.index!))
    && !OFFER_NEGATOR_INSIDE_RE.test(offer[0]))
    ? 'offer' : 'negated';
}

/**
 * The document answers a utility-letter question with "No" ("Has Entergy
 * issued a formal Interconnection Agreement or Capacity Letter confirming
 * that commitment? No."), or states that no such letter has been issued.
 * A question about what a letter requires or contains is not such an answer.
 */
function saysNoUtilityLetter(body: string): boolean {
  if (NO_UTILITY_LETTER_STATEMENT_RE.test(body)) return true;
  return [...body.matchAll(QUESTION_ANSWERED_NO_RE)].some(([, question]) => UTILITY_LETTER_NOUN_RE.test(question!)
    && LETTER_ISSUED_OR_EXISTS_RE.test(question!)
    && !LETTER_REQUIREMENT_QUESTION_RE.test(question!)
    && !NON_ELECTRIC_SERVICE_RE.test(question!));
}

/**
 * A row of the customer's own load ramp / schedule: the line is a schedule row
 * ("Phase 3 (Mid-2032): Approximately 1,000MW …") and the first non-row line
 * above it is a load-ramp / schedule header ("Richland Parish Load ramp:")
 * that offers nothing. A header on the same line as the figure does not
 * count: flattened PDF pages put a whole data sheet on one line, and the
 * utility's own indication can follow the customer's ramp there.
 */
function customerLoadRowAt(ix: BodyIndex, index: number): boolean {
  const lineNo = Math.max(0, lastAtOrBefore(ix.lineStarts, index));
  if (!SCHEDULE_ROW_RE.test(ix.lines[lineNo] ?? '')) return false;
  for (let above = lineNo - 1, seen = 0; above >= 0 && seen < 12; above -= 1) {
    const candidate = ix.lines[above]!;
    if (!candidate.trim()) continue;
    seen += 1;
    if (SCHEDULE_ROW_RE.test(candidate)) continue;
    return CUSTOMER_LOAD_HEADER_RE.test(candidate) && statesOffer(candidate) === 'none';
  }
  return false;
}

/**
 * What one quote of a figure says about a utility offer: an offer statement;
 * a denial (a negated offer, or a question answered "No"); a row of the
 * customer's load ramp; or none of these.
 */
function quoteRole(ix: BodyIndex, index: number): 'offer' | 'denial' | 'load' | 'other' {
  const { text, end } = sentenceRangeAt(ix, index);
  const offer = statesOffer(text);
  if (offer === 'offer') return 'offer';
  if (offer === 'negated') return 'denial';
  if (/\?\s*$/.test(text)) {
    ANSWER_NO_AT_RE.lastIndex = end;
    if (ANSWER_NO_AT_RE.test(ix.body)) return 'denial';
  }
  return customerLoadRowAt(ix, index) ? 'load' : 'other';
}

/** Reject a water utility or email date being passed off as an electric service offer. */
export function filterUtilityLetterFields(
  fields: Record<string, StructuredEvidenceValue>,
  body: string,
): void {
  const electric = /\b(?:electric(?:al|ity)?|grid|substation|transmission|interconnection|kV|MVA)\b/i;
  const water = /\b(?:water|wastewater|sewer|sewage|MGD|gallons)\b/i;
  if (water.test(body) && !electric.test(body)) {
    for (const key of Object.keys(fields)) delete fields[key];
    return;
  }

  // A document that says the utility has issued NO letter or agreement, or a
  // figure that only appears in the CUSTOMER's own load ramp, is not a
  // utility offer: it cannot fill utility-offered capacity, class of service
  // or a service date unless the same document states the offer itself.
  // Live 2026-09-29 (Holly Ridge Q&A): "Has Entergy issued a formal
  // Interconnection Agreement or Capacity Letter confirming that commitment?
  // No." sat above the block "Richland Parish Load ramp: … Phase 3
  // (Mid-2032): Approximately 1,000MW (Entergy Grid Power, Battery)". The
  // extractor filled capacityOffered_MW 1000 and serviceDate Mid-2032, and
  // every fresh feasibility report then described "a utility service letter
  // offering 1000 MW by mid-2032", held five times by the grounding gate.
  //
  // Kept narrow (review 2026-09-30), because the cached-suggestion sweep
  // re-runs this filter on every site's SED refresh:
  //  - only a question that asks whether a utility letter/agreement has been
  //    issued or exists counts as a no-letter answer ("Is a signed service
  //    agreement required before the study? No." does not);
  //  - a figure is customer load only as a row under an explicit load-ramp /
  //    schedule header, never for a generic "data center load" or "requested
  //    load" mention, which real utility letters quote back;
  //  - the figure's own sentence keeps it when it states an offer ("is able to
  //    provide", "has sufficient capacity to serve", "available to serve",
  //    "accommodate" …), unless the sentence is a question or negates it.
  // Every field is decided from the document alone, never from another
  // field, because the cached-suggestion sweep (`pruneUnverifiedSuggestions`)
  // re-checks one key at a time and must reach the same answer as a fresh
  // extraction.
  const ix = indexBody(body);
  const noUtilityLetter = saysNoUtilityLetter(body);
  const hasOfferStatement = [...body.matchAll(/[^.!?\n]+[.!?]?/g)]
    .some(sentence => statesOffer(sentence[0]) === 'offer' && electric.test(sentence[0]));
  const mwRoles = [...body.matchAll(MW_FIGURE_RE)].map(match => ({
    value: Number(match[1]!.replace(/,/g, '')),
    role: quoteRole(ix, match.index!),
  }));

  // A developer's tentative indication is a source statement, not a utility
  // offer. Keep the quoted number in the document ledger; do not promote it
  // into service-offer fields simply because the model named the utility.
  if (typeof fields.capacityOffered_MW === 'number') {
    const capacity = fields.capacityOffered_MW;
    const witnessMatches = [...body.matchAll(/(\d[\d,]*(?:\.\d+)?)\s*MW\b/gi)]
      .filter(match => Number(match[1]!.replace(/,/g, '')) === capacity);
    const witnesses = witnessMatches
      .map(match => body.slice(Math.max(0, match.index! - 120), match.index! + match[0].length + 100));
    const tentative = /\b(?:indication\s+from\s+(?:the\s+)?utility|utility\s+indication|tentative|unconfirmed|subject\s+to\s+(?:a\s+)?(?:study|confirmation))\b/i;
    const explicitOffer = witnesses.some(text => !tentative.test(text) && electric.test(text)
      && /\b(?:we|utility|cooperative)\b[^.!?\n]{0,70}\b(?:offer|commit|allocate|can\s+supply)\b/i.test(text));
    if (witnesses.some(text => tentative.test(text)) && !explicitOffer) {
      delete fields.capacityOffered_MW;
      delete fields.interconnectionClass;
    } else {
      const roles = mwRoles.filter(quote => quote.value === capacity).map(quote => quote.role);
      const offered = roles.includes('offer');
      const neverOffered = roles.length > 0 && roles.every(role => role === 'load' || role === 'denial');
      if (!offered && (noUtilityLetter || neverOffered)) delete fields.capacityOffered_MW;
    }
  }
  // A class of service comes from a utility offer. With no offer statement in
  // the document, and either a no-letter answer or no MW figure outside the
  // customer's load ramp (or a denied offer), there is nothing to classify.
  if (fields.interconnectionClass !== undefined && !hasOfferStatement) {
    const noOfferedFigure = mwRoles.length > 0 && mwRoles.every(quote => quote.role === 'load' || quote.role === 'denial');
    if (noUtilityLetter || noOfferedFigure) delete fields.interconnectionClass;
  }

  // A scalar cost must be explicitly stated for electric service. Do not
  // choose a range endpoint/midpoint or silently add separate upgrade ranges.
  const cost = fields.costEstimate_USD;
  if (typeof cost === 'number') {
    const money = /(?:\$|USD\s*)(\d[\d,]*(?:\.\d+)?)\s*(million|billion|thousand|[MKB])?\b/gi;
    let supported = false;
    for (const match of body.matchAll(money)) {
      const multiplier = /^(?:m|million)$/i.test(match[2] ?? '') ? 1e6
        : /^(?:b|billion)$/i.test(match[2] ?? '') ? 1e9
        : /^(?:k|thousand)$/i.test(match[2] ?? '') ? 1e3 : 1;
      if (Number(match[1]!.replace(/,/g, '')) * multiplier !== cost) continue;
      const before = body.slice(Math.max(0, match.index! - 120), match.index!);
      const after = body.slice(match.index! + match[0].length, match.index! + match[0].length + 80);
      const rangeEdge = /(?:[-–—]|\bto)\s*$/i.test(before) || /^\s*(?:[-–—]|to\b)\s*\$?\s*\d/i.test(after);
      if (!rangeEdge && electric.test(before + match[0] + after)) supported = true;
    }
    if (!supported) delete fields.costEstimate_USD;
  }

  // A dated message does not establish an energization date. Require a local
  // service-date statement when the value otherwise matches message metadata.
  const serviceDate = fields.serviceDate;
  if (typeof serviceDate === 'string') {
    const dateKey = (value: string): string | undefined => {
      const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (iso) return `${iso[1]}-${Number(iso[2])}-${Number(iso[3])}`;
      const parsed = new Date(value);
      return Number.isFinite(parsed.getTime())
        ? `${parsed.getFullYear()}-${parsed.getMonth() + 1}-${parsed.getDate()}`
        : undefined;
    };
    const valueDate = dateKey(serviceDate);
    const lines = body.split(/\r?\n/);
    const dateTokens = /\b(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}\/\d{1,2}\/\d{2,4}|(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2},?\s+\d{4})\b/gi;
    const matchesDate = (line: string): boolean => {
      if (line.includes(serviceDate)) return true;
      const dates = line.match(dateTokens) ?? [];
      return valueDate !== undefined && dates.some(date => dateKey(date) === valueDate);
    };
    const metadataMatch = lines.some(line => /^(?:date|sent|from|subject):/i.test(line.trim()) && matchesDate(line));
    const serviceWording = /\b(?:in[- ]service|service\s+date|energiz(?:ation|e|ed)|operational\s+date|power\s+available)\b/i;
    const serviceMatch = lines.some(line => serviceWording.test(line) && matchesDate(line));
    if (metadataMatch && !serviceMatch) delete fields.serviceDate;
    // With no utility letter, or when every line quoting the date is a row of
    // the customer's load ramp, the date is the customer's plan. Keep it only
    // when a statement gives it as the electric service / in-service date or
    // offers service by it (a question does not).
    const quotesDate = (line: string): boolean => matchesDate(line)
      || line.toLowerCase().includes(serviceDate.toLowerCase());
    /** Every place a line quotes the date: verbatim, or as an equal parsed date. */
    const datePositions = (line: string): number[] => {
      const lower = line.toLowerCase();
      const needle = serviceDate.toLowerCase();
      const at: number[] = [];
      for (let found = lower.indexOf(needle); found >= 0; found = lower.indexOf(needle, found + 1)) at.push(found);
      for (const date of line.matchAll(dateTokens)) {
        if (valueDate !== undefined && dateKey(date[0]) === valueDate) at.push(date.index!);
      }
      return at.length > 0 ? at : [0];
    };
    const dateQuotes: number[] = [];
    ix.lines.forEach((rawLine, lineNo) => {
      if (quotesDate(rawLine)) dateQuotes.push(...datePositions(rawLine).map(at => ix.lineStarts[lineNo]! + at));
    });
    const customerLoadDate = dateQuotes.length > 0 && dateQuotes.every(at => customerLoadRowAt(ix, at));
    const statedAsServiceDate = dateQuotes.some(at => {
      const { text } = sentenceRangeAt(ix, at);
      return !/\?\s*$/.test(text) && quotesDate(text)
        && (serviceWording.test(text) || statesOffer(text) === 'offer');
    });
    if (fields.serviceDate !== undefined && (noUtilityLetter || customerLoadDate) && !statedAsServiceDate) {
      delete fields.serviceDate;
    }
  }
}
